import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart, TooltipContainer, TooltipInterfaceProps } from '@frontend/charts';
import { Divider } from '@frontend/divider';
import { Trans, useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Chip, Text } from '@frontend/design-system';

interface ScheduleOpportunityChartProps {
  scheduledCount: number;
  scheduledRate: number;
  unscheduledCount: number;
  unscheduledRate: number;
  totalOpportunities: number;
  totalLocations: number;
}

export const ScheduleOpportunityChart: FC<ScheduleOpportunityChartProps> = (props) => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const { scheduledCount, unscheduledCount, totalOpportunities } = props;
  const navigate = useNavigate();
  const { chartOutlineColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
    'dataColors',
    'dataLabels',
    'chartOutlineColors'
  );

  const isLoading = false; // Replace with actual loading state if needed
  const chartData = useMemo(() => {
    // Show 100% for no opportunities if totalOpportunities is 0
    const noOpportunityCount = totalOpportunities === 0 ? 100 : 0;

    return {
      data: {
        groups: [
          {
            name: 'opportunities',
            values: {
              [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: scheduledCount,
              [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: unscheduledCount,
              [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: noOpportunityCount,
            },
          },
        ],
      },
      maxValue: totalOpportunities || 100,
      [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: scheduledCount,
      [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: unscheduledCount,
      [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: noOpportunityCount,
    };
  }, [scheduledCount, totalOpportunities, unscheduledCount]);

  const showEmptyBar = totalOpportunities === 0;

  const handleChartClick = (type: CallIntelTypes.SchedulingOutcomeEnum) => {
    const filters = {
      schedulingOpportunity: CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED,
      schedulingOutcomes: type === CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN ? [] : [type],
    };

    navigate({ to: '/analytics/call-intelligence/main', search: { filters: btoa(JSON.stringify(filters)) } });
  };

  return (
    <div css={styles.chartsContainer}>
      <Chart
        colors={dataColors.schedulingOutcome}
        strokeColors={chartOutlineColors.schedulingOutcome}
        commonTooltipLabel={t('Calls')}
        isLoading={isLoading}
        noBorder
        emptyStateConfig={{
          hideEmptyState: true,
        }}
        style={{ padding: '0px', margin: '0px' }}
        labels={dataLabels.schedulingOutcome}
      >
        <Chart.BarChart
          tooltipRenderer={renderOpportunityTooltipData(props)}
          appearance={{
            barSize: 40,
            barRadius: 4,
            hideTooltip: totalOpportunities === 0,
            barsGap: 8,
            groupsGap: 8,
            margin: { left: 8, right: 8 },
            layout: 'vertical',
            maxValue: chartData.maxValue,
            mode: 'stacked',
            alwaysApplyBarBorders: !showEmptyBar, // Apply borders only if there are opportunities
          }}
          onClick={
            !showEmptyBar ? (props) => handleChartClick(props.barId as CallIntelTypes.SchedulingOutcomeEnum) : undefined
          }
          data={chartData.data}
          formatValue={formatters.value}
        />
      </Chart>
    </div>
  );
};

const renderOpportunityTooltipData = ({
  totalLocations,
  totalOpportunities,
  scheduledRate,
  unscheduledRate,
}: ScheduleOpportunityChartProps) =>
  function OpportunityTooltip(props: TooltipInterfaceProps) {
    const { id, formattedValue } = props.data[0];
    const { t } = useTranslation('callIntelDashboardWidgets');
    const { chartLabelColors, dataLabels } = useCallIntelDefaultsShallowStore('chartLabelColors', 'dataLabels');

    const label = dataLabels.schedulingOutcome?.[id as CallIntelTypes.SchedulingOutcomeEnum] ?? '';
    const fontColor =
      chartLabelColors.schedulingOutcome?.[id as CallIntelTypes.SchedulingOutcomeEnum] ?? theme.colors.neutral50;

    const rate = id === 'scheduledCount' ? scheduledRate : unscheduledRate;

    if (id === 'noOpportunityCount') {
      return null;
    }
    return (
      <TooltipContainer {...props}>
        <div css={styles.tooltipContainer}>
          <Text size='medium' color='subdued'>
            {label}
          </Text>
          <Chip.Location>{t('{{count}} Locations', { count: totalLocations })}</Chip.Location>
          <Divider margin={0} />
          <Text size='medium'>
            <Trans t={t}>
              <Text as='span' weight='bold' css={{ color: fontColor }}>
                {{ formattedValue }}
              </Text>{' '}
              {{ label }}
            </Trans>
          </Text>
          <Text size='medium' color='subdued'>
            {t('Out of {{count}} Opportunities', { count: totalOpportunities })}
          </Text>
          <Divider margin={0} />
          <Text size='medium'>
            <Trans t={t}>
              <Text as='span' weight='bold' css={{ color: fontColor }}>
                {{ rate }}%
              </Text>{' '}
              {{ label }} Rate
            </Trans>
          </Text>
          <Divider margin={0} />
          <Text size='medium' color='subdued' css={{ fontStyle: 'italic' }}>
            {t('Click the segment to learn more')}
          </Text>
        </div>
      </TooltipContainer>
    );
  };

const styles = {
  chartsContainer: css`
    align-self: center;
    flex: 1;
    @container (max-width: ${breakpoints.small.min}px) {
      flex: 0;
      width: 100%;
    }
  `,
  tooltipContainer: css`
    background-color: ${theme.colors.white};
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    gap: ${theme.spacing(1)};
  `,
};
